import EmpreenderIcon from '../ui/appsIcons/EmpreenderIcon';
import ReplayIcon from '../ui/appsIcons/ReplayIcon';
import { Button } from '../ui/Button';
import { ReactElement } from 'react';
import { motion } from 'framer-motion';
import { twMerge } from 'tailwind-merge';
import RastreioIcon from '../ui/appsIcons/RastreioIcon';
import DropiIcon from '../ui/appsIcons/DropiIcon';
import SuperCheckoutIcon from '../ui/appsIcons/SuperCheckout';
import LilyIcon from '../ui/appsIcons/LilyIcon';
import SakIcon from '../ui/appsIcons/SakIcon';
import MelhorApp from '../ui/appsIcons/MelhorApp';
import SellyIcon from '../ui/appsIcons/SellyIcon';
import { OrbitingCircles } from '../ui/OrbitingCircles/OrbitingCircles';
import OvniIcon from '../ui/appsIcons/OvniIcon';
import SuperFavoritoIcon from '../ui/appsIcons/SuperFavoritoIcon';
import WhatslyIcon from '../ui/appsIcons/WhatslyIcon';
import ChegueiIcon from '../ui/appsIcons/ChegueiIcon';
import CampoPersonalizadoIcon from '../ui/appsIcons/CampoPersonalizadoIcon';
import BotaoZapIcon from '../ui/appsIcons/BotaoZap';
import FreteHero from '../ui/appsIcons/FreteHero';
import App365Icon from '../ui/appsIcons/365';
import DashIcon from '../ui/appsIcons/Dash';
import PromocaoAgendadaIcon from '../ui/appsIcons/PromocaoAgendadaIcon';
import DijitalIcon from '../ui/appsIcons/Dijital';
import DropDigitalIcon from '../ui/appsIcons/DropDigitalIcon';
import ParcelamentoIcon from '../ui/appsIcons/ParcelamentoIcon';
import AutoMagicoIcon from '../ui/appsIcons/automagico';
import { useTranslation } from '../lib/i18n/TranslationContext';

const orbitLayers = [
	{
		radius: 300,
		speed: 0.35,
		delay: 0,
		reverse: false,
		apps: [
			{ name: '365 Artes', icon: <App365Icon />, link: 'https://365artes.com.br/' },
			{ name: 'Sak', icon: <SakIcon />, link: 'https://get.sak.com.br/' },
			{ name: 'Landingpage', icon: <MelhorApp />, link: 'https://landingpage.com.br/' },
			{ name: 'Replay', icon: <ReplayIcon />, link: 'https://replay.com.br/' },
			{ name: 'Dropi', icon: <DropiIcon />, link: 'https://dropi.com.br' },
			{ name: 'Ovni Influenciadores', icon: <OvniIcon />, link: 'https://ovni.com.br/' },
			{ name: 'Dash', icon: <DashIcon />, link: 'https://app.dash.com.br/' },
			{ name: 'lily Reviews', icon: <LilyIcon />, link: 'https://lily.com.br/' },
			{ name: 'Rastreio', icon: <RastreioIcon />, link: 'https://get.rastreio.net' },
			{ name: 'SuperCheckout', icon: <SuperCheckoutIcon />, link: 'https://www.supercheckout.com.br' },
			{ name: 'Selly', icon: <SellyIcon />, link: 'https://selly.com.br' },
			{ name: 'Drop.Digital', icon: <DropDigitalIcon />, link: 'https://dropdigital.com.br/' },
			{ name: 'Parcelamento', icon: <ParcelamentoIcon />, link: 'https://parcelamento.com/' },
			{ name: 'Botão Zap', icon: <BotaoZapIcon />, link: 'https://empreender.plus' },
		],
	},
	{
		radius: 195,
		speed: 0.6,
		delay: -20,
		reverse: true,
		apps: [
			{ name: 'Dropi', icon: <DropiIcon />, link: 'https://dropi.com.br' },
			{ name: 'Replay', icon: <ReplayIcon />, link: 'https://replay.com.br/' },
			{ name: 'Selly', icon: <SellyIcon />, link: 'https://selly.com.br' },
			{ name: 'Cheguei', icon: <ChegueiIcon />, link: 'https://cheguei.com.br' },
			{ name: 'Automágico', icon: <AutoMagicoIcon />, link: 'https://automagico.com.br/' },
			{ name: 'Promoção agendada', icon: <PromocaoAgendadaIcon />, link: 'https://empreender.plus' },
			{ name: 'Campo Personalizado', icon: <CampoPersonalizadoIcon />, link: 'https://campopersonalizado.com.br' },
			{ name: 'Dijital', icon: <DijitalIcon />, link: 'https://dijital.com.br' },
			{ name: 'Whats.ly', icon: <WhatslyIcon />, link: 'https://get.whats.ly' },
			{ name: 'Super Favoritos', icon: <SuperFavoritoIcon />, link: 'https://empreender.plus' },
		],
	},
	{
		radius: 103,
		speed: 0.9,
		delay: -20,
		reverse: false,
		apps: [
			{ name: 'Sak', icon: <SakIcon />, link: 'https://get.sak.com.br' },
			{ name: 'Selly', icon: <SellyIcon />, link: 'https://selly.com.br' },
			{ name: 'Cheguei', icon: <ChegueiIcon />, link: 'https://cheguei.com.br' },
			{ name: 'Frete Hero', icon: <FreteHero />, link: 'https://fretehero.com.br' },
		],
	},
];

const ContainerApp = ({ appName, icon, link, className }: { className?: string; appName: string; icon: ReactElement; link: string }) => {
	const { t } = useTranslation();
	return (
		<motion.a
			whileHover={{
				scale: 1.03,
				transition: { duration: 0.1 },
			}}
			href={link}
			target='_blank'
			className={twMerge(' bg-white rounded-full px-3 py-1 absolute cursor-pointer', className)}>
			<div className='h-6 flex gap-2 flex-row items-center justify-start'>
				<span className='flex-shrink-0 size-6'>{icon}</span>

				<span className='text-gray-600 font-normal text-[10.595px] leading-[15.136px] not-italic whitespace-nowrap'>{t(appName)}</span>
			</div>
		</motion.a>
	);
};

const CardApps = () => {
	const { t } = useTranslation();

	return (
		<div className='flex flex-row flex-wrap items-center justify-center gap-4 max-w-5xl w-full'>
			<div className='[container-type:inline-size] bg-gray-100 w-[96%] h-[300px] rounded-lg flex items-center justify-center overflow-hidden relative'>
				<div className='w-full max-w-[450px] px-4 pointer-events-none flex justify-center items-center flex-col gap-3 relative z-10'>
					<EmpreenderIcon className='h-4' />

					<div className='flex flex-col'>
						<p className='text-center text-[40px] font-black leading-normal uppercase text-gray-900'>{t('Mais de 20 Apps')}</p>
						<p className='text-center text-[40px] font-black leading-[0.4] uppercase text-gray-900'>{t('Pelo preço de 1')}</p>
					</div>

					<Button asChild className='mt-5 pointer-events-auto' variant='secondary'>
						<a href='https://app.empreender.plus/promocoes?main_promo=1' target='_blank'>
							{t('Assine agora')}
						</a>
					</Button>
				</div>

				<div
					className="h-full absolute top-0 w-full select-none blur-[1px] bg-no-repeat bg-cover bg-center [@container_(min-width:960px)]:blur-0 [@container_(min-width:960px)]:relative [@container_(min-width:960px)]:w-[533px]
             bg-none [@container_(min-width:960px)]:bg-[url('https://empreender.nyc3.digitaloceanspaces.com/LP/im/5efe8f55307b.png')]">
					<img loading='lazy' className='hidden [@container_(min-width:960px)]:block object-cover pointer-events-none h-full relative z-20' draggable={false} src='https://empreender.nyc3.digitaloceanspaces.com/landingpage/landingpage/s717t6010541.png' alt='' />

					<div className='absolute top-0 flex w-full h-full [@container_(min-width:960px)]:!w-[627px] [@container_(min-width:960px)]:h-[639px] flex-col items-center justify-center'>
						{orbitLayers.map((layer, i) => (
							<OrbitingCircles key={i} radius={layer.radius} speed={layer.speed} delay={layer.delay} reverse={layer.reverse}>
								{layer.apps.map((app, j) => (
									<ContainerApp key={j} link={app.link} icon={app.icon} appName={app.name} />
								))}
							</OrbitingCircles>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};

export default CardApps;
